import { useEffect, useRef, useState } from 'react';
import { Plus, Copy, ExternalLink, Trash2, Users, FileText, Image as ImageIcon, Download, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import { Badge } from '../ui/Badge';

const TIPOS_PARCEIRO = [
  { key: 'patrocinador', label: 'Patrocinador', color: 'purple' },
  { key: 'parceiro', label: 'Parceiro', color: 'info' },
  { key: 'apoiador', label: 'Apoiador', color: 'success' },
  { key: 'fornecedor', label: 'Fornecedor', color: 'warning' },
];

function tipoLabel(k) { return TIPOS_PARCEIRO.find((t) => t.key === k)?.label || k; }
function tipoColor(k) { return TIPOS_PARCEIRO.find((t) => t.key === k)?.color || 'default'; }

export function ParceirosSection({ demanda, podeEditar }) {
  const [lista, setLista] = useState([]);
  const [adding, setAdding] = useState(false);
  const [expandido, setExpandido] = useState(null);

  const appOrigin = window.location.origin;
  const load = () => api.get(`/demandas/${demanda.codigo}/parceiros`).then((r) => setLista(r.parceiros));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [demanda.codigo]);

  const copy = (t) => { navigator.clipboard.writeText(`${appOrigin}/parceiro/${t}`); };

  const remover = async (id) => {
    if (!confirm('Remover este parceiro? As mídias enviadas serão apagadas.')) return;
    await api.del(`/demandas/${demanda.codigo}/parceiros/${id}`);
    load();
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong><Users size={14} style={{ verticalAlign: 'middle' }} /> Parceiros e patrocinadores</strong>
          <div className="text-xs muted">Gere um link para cada parceiro enviar mídias (fotos, vídeos) diretamente.</div>
        </div>
        {podeEditar && !adding && (
          <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
            <Plus size={14} /> Adicionar
          </button>
        )}
      </div>
      <div className="card-body">
        {adding && (
          <AddForm
            demandaId={demanda.codigo}
            onCancel={() => setAdding(false)}
            onCreated={() => { setAdding(false); load(); }}
          />
        )}

        {lista.length === 0 ? (
          <div className="text-sm muted">Nenhum parceiro cadastrado ainda.</div>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {lista.map((p) => (
              <ParceiroItem
                key={p.id}
                parceiro={p}
                demandaId={demanda.codigo}
                podeEditar={podeEditar}
                expandido={expandido === p.id}
                onToggle={() => setExpandido(expandido === p.id ? null : p.id)}
                onCopyLink={() => copy(p.token)}
                onRemove={() => remover(p.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ParceiroItem({ parceiro: p, demandaId, podeEditar, expandido, onToggle, onCopyLink, onRemove }) {
  const [midias, setMidias] = useState(null);
  const link = `${window.location.origin}/parceiro/${p.token}`;

  useEffect(() => {
    if (expandido && !midias) {
      api.get(`/demandas/${demandaId}/parceiros/${p.id}/midias`).then((r) => setMidias(r.midias));
    }
  }, [expandido, midias, p.id, demandaId]);

  const fmtSize = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)}KB` : `${(n / (1024 * 1024)).toFixed(1)}MB`;

  return (
    <div style={{ padding: 12, border: '1px solid var(--c-border)', borderRadius: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            <Badge color={tipoColor(p.tipo)}>{tipoLabel(p.tipo)}</Badge>
            <strong style={{ fontSize: 14 }}>{p.nome}</strong>
            {p.midias_count > 0 && (
              <button onClick={onToggle} className="badge success" style={{ cursor: 'pointer', border: 'none' }}>
                📎 {p.midias_count} mídia{p.midias_count > 1 ? 's' : ''}
              </button>
            )}
            {!p.ativo && <Badge color="default">inativo</Badge>}
          </div>
          {p.contato && <div className="text-xs muted">{p.contato}</div>}
          {p.observacoes && <div className="text-sm" style={{ marginTop: 4 }}>{p.observacoes}</div>}
        </div>
        {podeEditar && (
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onRemove} title="Remover">
            <Trash2 size={14} style={{ color: 'var(--c-danger)' }} />
          </button>
        )}
      </div>

      <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={onCopyLink}>
          <Copy size={13} /> Copiar link
        </button>
        <a className="btn btn-secondary btn-sm" href={link} target="_blank" rel="noreferrer">
          <ExternalLink size={13} /> Abrir
        </a>
        {p.midias_count > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={onToggle}>
            {expandido ? 'Ocultar mídias' : 'Ver mídias'}
          </button>
        )}
      </div>

      {expandido && midias && midias.length > 0 && (
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
          {midias.map((m) => {
            const isImg = m.mime?.startsWith('image/');
            const isVideo = m.mime?.startsWith('video/');
            return (
              <a key={m.id} href={m.url} target="_blank" rel="noreferrer" style={{ display: 'block', border: '1px solid var(--c-border)', borderRadius: 8, overflow: 'hidden', background: 'var(--c-surface-2)', textDecoration: 'none' }}>
                <div style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                  {isImg ? (
                    <img src={m.url} alt={m.nome_original} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : isVideo ? (
                    <video src={m.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                  ) : (
                    <FileText size={32} color="#fff" />
                  )}
                </div>
                <div style={{ padding: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-text)' }} className="truncate">{m.nome_original}</div>
                  <div style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>{fmtSize(m.tamanho)} · {format(new Date(m.created_at), 'dd/MM HH:mm')}</div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddForm({ demandaId, onCancel, onCreated }) {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('parceiro');
  const [contato, setContato] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [expiraDias, setExpiraDias] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!nome.trim()) { setErr('Informe o nome.'); return; }
    setSaving(true);
    try {
      const body = { nome, tipo, contato: contato || null, observacoes: observacoes || null };
      if (expiraDias) body.expira_em_dias = parseInt(expiraDias, 10);
      await api.post(`/demandas/${demandaId}/parceiros`, body);
      onCreated?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 10, marginBottom: 14, padding: 12, background: 'var(--c-surface-2)', borderRadius: 10 }}>
      <input className="input" required placeholder="Nome do parceiro (ex: Unifenas, Cleitinho, Sindicato Rural)" value={nome} onChange={(e) => setNome(e.target.value)} />
      <div className="row-wrap" style={{ gap: 6 }}>
        {TIPOS_PARCEIRO.map((t) => (
          <button type="button" key={t.key} className={`btn btn-sm ${tipo === t.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTipo(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <input className="input" placeholder="Contato (WhatsApp, e-mail — opcional)" value={contato} onChange={(e) => setContato(e.target.value)} />
      <textarea className="textarea" rows={2} placeholder="Instruções para o parceiro (opcional)" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
      <div className="row" style={{ gap: 10 }}>
        <label className="text-sm muted">Expira em (dias, vazio = sem limite):</label>
        <input className="input" type="number" min="1" max="180" style={{ maxWidth: 100 }} value={expiraDias} onChange={(e) => setExpiraDias(e.target.value)} />
      </div>
      {err && <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Salvando...' : 'Gerar link'}</button>
      </div>
    </form>
  );
}
